/**
 * Bundle Cart Banner
 *
 * 1) PRUNE: Removes stale banners when bundle items are deleted from cart.
 *
 * 2) APPLY: Collects ALL bundle discount codes from visible banners +
 *    any already-applied discounts, then sends them comma-separated via
 *    /cart/update.js ({ discount: 'BNRUNNER,BNDECOR' }) so multiple
 *    bundle discounts coexist. /cart/update.js answers 200 even when a code
 *    doesn't qualify, so each code's `applicable` flag in the response
 *    decides whether its banner shows "applied" or "not available".
 */

/* The AJAX cart exposes codes as `discount_codes: [{ code, applicable }]`;
   there is no `discount_applications` key on /cart.js. */
const bundleBannerIcons = {
  arrow: '<svg class="icon br-icon br-icon--arrow-right icon--inline" width="14" height="14" role="presentation" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" focusable="false"><path d="M221.66,133.66l-72,72a8,8,0,0,1-11.32-11.32L196.69,136H40a8,8,0,0,1,0-16H196.69L138.34,61.66a8,8,0,0,1,11.32-11.32l72,72A8,8,0,0,1,221.66,133.66Z"/></svg>',
  check: '<svg class="icon br-icon br-icon--check icon--inline" width="14" height="14" role="presentation" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" focusable="false"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/></svg>',
  warning: '<svg class="icon br-icon br-icon--warning" width="20" height="20" role="presentation" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" focusable="false"><path d="M235.07,189.09,147.61,37.22h0a22.75,22.75,0,0,0-39.22,0L20.93,189.09a21.53,21.53,0,0,0,0,21.72A22.35,22.35,0,0,0,40.55,222h174.9a22.35,22.35,0,0,0,19.6-11.19A21.53,21.53,0,0,0,235.07,189.09ZM224.66,204.8a10.46,10.46,0,0,1-9.21,5.2H40.55a10.46,10.46,0,0,1-9.21-5.2,9.51,9.51,0,0,1,0-9.72L118.79,43.21a10.75,10.75,0,0,1,18.42,0l87.46,151.87A9.51,9.51,0,0,1,224.66,204.8ZM122,144V104a6,6,0,0,1,12,0v40a6,6,0,0,1-12,0Zm16,36a10,10,0,1,1-10-10A10,10,0,0,1,138,180Z"/></svg>',
};

function cartCodes(cart) {
  return (cart?.discount_codes || []).map(d => d.code);
}

function isCodeApplicable(cart, code) {
  const wanted = String(code).toUpperCase();
  return (cart?.discount_codes || []).some(
    d => String(d.code).toUpperCase() === wanted && d.applicable
  );
}

async function postCartDiscount(codes) {
  const cartUpdateUrl = window.theme?.routes?.cartUpdate || '/cart/update.js';
  const response = await fetch(cartUpdateUrl, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ discount: codes.join(',') })
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

/* Adds `newCodes` to the cart's existing codes and reports which ones
   Shopify actually applied. Rejected codes are stripped again so they don't
   linger in the cart as a dead code. */
async function submitBundleCodes(newCodes) {
  const cartUrl = window.theme?.routes?.cart || '/cart';
  const cartResponse = await fetch(cartUrl + '.js', { credentials: 'same-origin' });
  let cart = await cartResponse.json();

  const merged = cartCodes(cart);
  let changed = false;
  for (const code of newCodes) {
    if (!merged.includes(code)) {
      merged.push(code);
      changed = true;
    }
  }
  if (changed) cart = await postCartDiscount(merged);

  const applied = newCodes.filter(code => isCodeApplicable(cart, code));
  const rejected = newCodes.filter(code => !applied.includes(code));

  if (rejected.length) {
    const rejectedUpper = rejected.map(code => code.toUpperCase());
    await postCartDiscount(
      cartCodes(cart).filter(code => !rejectedUpper.includes(String(code).toUpperCase()))
    );
  }

  return { applied, rejected };
}

/* ── Prune banners when line items change ── */

async function recountAndPruneBundles(cart) {
  const codeCounts = Object.create(null);
  if (cart?.items) {
    for (const item of cart.items) {
      const code = item?.properties?._bundle_campaign;
      if (code) codeCounts[code] = (codeCounts[code] || 0) + 1;
    }
  }

  // Broken bundles are read from the cart lines, not only from banners — a
  // banner may be closed by the user or not rendered at all (Liquid skips
  // it once the code is applied), and its code still has to be removed.
  const removedCodes = Object.keys(codeCounts).filter(code => codeCounts[code] < 2);

  for (const banner of document.querySelectorAll('[data-bundle-cart-banner]')) {
    const code = banner.dataset.code;
    if (!code || (codeCounts[code] || 0) < 2) {
      if (code && !removedCodes.includes(code)) removedCodes.push(code);
      banner.remove();
    }
  }

  // Remove orphaned discount codes from cart.
  if (removedCodes.length > 0) {
    const appliedCodes = cartCodes(cart);

    const remaining = appliedCodes.filter(c => !removedCodes.includes(c));

    // Only update if codes actually changed.
    if (remaining.length !== appliedCodes.length) {
      try {
        await postCartDiscount(remaining);
      } catch (err) {
        console.error('[bundle-cart-banner] discount removal failed:', err);
      }
    }
  }
}

document.addEventListener('on:line-item:change', (event) => {
  if (event?.detail?.cart) recountAndPruneBundles(event.detail.cart);
});

/* ── AJAX discount code application via /cart/update.js ── */

async function applyBundleDiscount(button) {
  const newCode = button.dataset.applyDiscount;
  if (!newCode) return;

  const banner = button.closest('[data-bundle-cart-banner]');

  button.disabled = true;
  button.textContent = 'Applying…';

  try {
    const { applied } = await submitBundleCodes([newCode]);

    if (!applied.length) {
      if (banner) setBannerUnavailable(banner);
      return;
    }

    // Refresh cart drawer so discounted prices appear.
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.refresh) await cartDrawer.refresh();

    if (banner) setBannerApplied(banner);
  } catch (err) {
    console.error('[bundle-cart-banner] discount apply failed:', err);
    button.disabled = false;
    button.innerHTML = `Apply Code ${bundleBannerIcons.arrow}`;
  }
}

document.addEventListener('click', (event) => {
  const dismissBtn = event.target.closest('[data-dismiss-bundle-banner]');
  if (dismissBtn) {
    dismissBtn.closest('[data-bundle-cart-banner]')?.remove();
    return;
  }

  const btn = event.target.closest('[data-apply-discount]');
  if (btn) applyBundleDiscount(btn);
});

/* ── Auto-apply on cart drawer open ──
   Banner presence already means the bundle is complete (≥2 line items sharing
   a `_bundle_campaign` code) and the code isn't applied yet — that check lives
   in bundle-cart-banner.liquid. Re-using the rendered banners as the source of
   truth keeps the auto-apply condition identical to the banner's render
   condition. The banner transitions through loading → applied (or
   unavailable, when Shopify reports the code as not applicable) in place,
   kept in the DOM so the user sees what happened, while the cart drawer
   refreshes its prices/summary in the background. */

let autoApplyInFlight = false;

function setBannerLoading(banner) {
  banner.classList.add('bundle-cart-banner--loading');
  const btn = banner.querySelector('[data-apply-discount]');
  if (btn) {
    if (btn.dataset.originalLabel == null) {
      btn.dataset.originalLabel = btn.innerHTML.trim();
    }
    btn.disabled = true;
    btn.textContent = 'Applying…';
  }
}

function setBannerApplied(banner) {
  banner.classList.remove('bundle-cart-banner--loading');
  banner.classList.add('bundle-cart-banner--applied');
  const title = banner.querySelector('.bundle-cart-banner__title');
  if (title) title.textContent = 'Discount Applied!';
  const desc = banner.querySelector('.bundle-cart-banner__desc');
  if (desc) desc.textContent = 'Your bundle discount has been applied to the cart.';
  const btn = banner.querySelector('[data-apply-discount]');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = `Applied ${bundleBannerIcons.check}`;
  }
}

function setBannerUnavailable(banner) {
  banner.classList.remove('bundle-cart-banner--loading');
  banner.classList.add('bundle-cart-banner--unavailable');
  const icon = banner.querySelector('.bundle-cart-banner__icon');
  if (icon) icon.innerHTML = bundleBannerIcons.warning;
  const title = banner.querySelector('.bundle-cart-banner__title');
  if (title) title.textContent = 'Discount Not Available';
  const desc = banner.querySelector('.bundle-cart-banner__desc');
  if (desc) {
    desc.textContent = `The ${banner.dataset.code} bundle discount doesn't apply to the items in your cart.`;
  }
  const btn = banner.querySelector('[data-apply-discount]');
  if (btn) btn.hidden = true;
}

function revertBannerLoading(banner) {
  banner.classList.remove('bundle-cart-banner--loading');
  const btn = banner.querySelector('[data-apply-discount]');
  if (btn) {
    btn.disabled = false;
    if (btn.dataset.originalLabel) btn.innerHTML = btn.dataset.originalLabel;
  }
}

async function autoApplyVisibleBundleBanners() {
  if (autoApplyInFlight) return;

  const banners = Array.from(document.querySelectorAll(
    '[data-bundle-cart-banner]:not(.bundle-cart-banner--applied):not(.bundle-cart-banner--unavailable):not(.bundle-cart-banner--loading)'
  ));
  if (!banners.length) return;

  const bannerCodes = [];
  for (const banner of banners) {
    const code = banner.dataset.code;
    if (code && !bannerCodes.includes(code)) bannerCodes.push(code);
  }
  if (!bannerCodes.length) return;

  // Paint the loading state immediately so the user sees the auto-apply
  // happening rather than a silent banner that suddenly flips to applied.
  for (const banner of banners) setBannerLoading(banner);

  autoApplyInFlight = true;
  try {
    const { applied } = await submitBundleCodes(bannerCodes);

    // Flip each banner to its outcome — kept in the DOM so the user sees
    // it. cart-drawer.refresh() only touches the cart-items + summary
    // regions (banner lives outside those), so the updated state survives
    // the price/summary refresh that runs next.
    for (const banner of banners) {
      if (applied.includes(banner.dataset.code)) {
        setBannerApplied(banner);
      } else {
        setBannerUnavailable(banner);
      }
    }

    if (applied.length) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer?.refresh) await cartDrawer.refresh();
    }
  } catch (err) {
    console.error('[bundle-cart-banner] auto-apply failed:', err);
    for (const banner of banners) revertBannerLoading(banner);
  } finally {
    autoApplyInFlight = false;
  }
}

document.addEventListener('on:cart-drawer:after-open', autoApplyVisibleBundleBanners);
